/**
 * Number of options a filter dropdown can hold before it renders a search field.
 * Shared so the attribute and value dropdowns never disagree on when to show one.
 */
export const DROPDOWN_SEARCH_THRESHOLD = 8;

/**
 * Standard attributes of the conversation model
 */
export const CONVERSATION_ATTRIBUTES = {
  STATUS: 'status',
  PRIORITY: 'priority',
  ASSIGNEE_ID: 'assignee_id',
  INBOX_ID: 'inbox_id',
  TEAM_ID: 'team_id',
  CONTACT_ID: 'contact_id',
  DISPLAY_ID: 'display_id',
  CAMPAIGN_ID: 'campaign_id',
  LABELS: 'labels',
  BROWSER_LANGUAGE: 'browser_language',
  REFERER: 'referer',
  CREATED_AT: 'created_at',
  LAST_ACTIVITY_AT: 'last_activity_at',
};

export const CONTACT_ATTRIBUTES = {
  NAME: 'name',
  EMAIL: 'email',
  PHONE_NUMBER: 'phone_number',
  IDENTIFIER: 'identifier',
  COUNTRY_CODE: 'country_code',
  CITY: 'city',
  COMPANY_NAME: 'company_name',
  CREATED_AT: 'created_at',
  LAST_ACTIVITY_AT: 'last_activity_at',
  REFERER: 'referer',
  BLOCKED: 'blocked',
  LABELS: 'labels',
};

/**
 * Determines the input type for a custom attribute based on its key
 * @param {string} key - The attribute display type key
 * [PROSPEK] 2026-06-21 list custom attributes need multi-select values in campaign audience builders
 * @returns {'date'|'plainText'|'searchSelect'|'multiSelect'|'booleanSelect'} The corresponding input type
 * [/PROSPEK]
 */
export const getCustomAttributeInputType = key => {
  switch (key) {
    case 'date':
      return 'date';
    case 'text':
      return 'plainText';
    case 'list':
      // [PROSPEK] 2026-06-21 allow list custom attributes to emit all selected values
      return 'multiSelect';
    // [/PROSPEK]
    case 'checkbox':
      // [PROSPEK] 2026-06-01 keep checkbox flow consistent with campaign builder select-based UI
      return 'searchSelect';
    // [/PROSPEK]
    default:
      return 'plainText';
  }
};

/**
 * Builds filter types for custom attributes
 * This also removes any conflicting attributes
 * @param {Array} attributes - The attributes array
 * @param {Function} getOperatorTypes - Function to get operator types
 * @returns {Array} Array of filter types
 */
export const buildAttributesFilterTypes = (
  attributes,
  getOperatorTypes,
  filterModel = 'conversation'
) => {
  const standardAttributes = Object.values(
    filterModel === 'conversation'
      ? CONVERSATION_ATTRIBUTES
      : CONTACT_ATTRIBUTES
  );

  return attributes
    .filter(attr => !standardAttributes.includes(attr.attributeKey))
    .map(attr => ({
      attributeKey: attr.attributeKey,
      value: attr.attributeKey,
      attributeName: attr.attributeDisplayName,
      label: attr.attributeDisplayName,
      // [PROSPEK] 2026-06-21 expose display type so checkbox filters can hide operators without affecting other searchSelect fields
      attributeDisplayType: attr.attributeDisplayType,
      // [/PROSPEK]
      inputType: getCustomAttributeInputType(attr.attributeDisplayType),
      filterOperators: getOperatorTypes(attr.attributeDisplayType),
      // [PROSPEK] 2026-06-01 add explicit checkbox option list for filter builder
      options: (() => {
        if (attr.attributeDisplayType === 'list') {
          return attr.attributeValues.map(item => ({ id: item, name: item }));
        }
        if (attr.attributeDisplayType === 'checkbox') {
          return [
            { id: true, name: 'Checked' },
            { id: false, name: 'Unchecked' },
          ];
        }
        return [];
      })(),
      // [/PROSPEK]
      attributeModel: 'customAttributes',
    }));
};

/**
 * Replaces underscores with spaces in a string
 * @param {string} text - The input string
 * @returns {string} The string with underscores replaced by spaces
 */
export const replaceUnderscoreWithSpace = text =>
  text?.replaceAll('_', ' ') ?? '';
